import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Plus, Search, ChevronLeft, ChevronRight, 
  RefreshCw, Lock, Users, ArrowRight 
} from 'lucide-react';
import toast from 'react-hot-toast';

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get('/api/roles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRoles(res.data);
    } catch (err) {
      toast.error("Failed to load security roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  // فلترة محلية سريعة للرولز (بما أن عددهم غالباً بيبقى قليل)
  const filteredRoles = roles.filter(role => 
    role.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full bg-surface-app text-[11px] font-bold uppercase tracking-tighter">

      {/* Toolbar */}
      <div className="bg-surface-card p-3 border-b border-line shadow-sm flex justify-between items-center sticky top-0 z-10">
        <div className="flex items-center gap-6 ml-4">
          <h2 className="text-primary text-lg italic font-black flex items-center gap-2">
            <Lock size={20} className="text-status-warning" /> SECURITY ROLES
          </h2>

          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-ink-secondary" size={14} />
            <input
              className="pl-9 pr-4 py-2 border border-line w-80 outline-none focus:border-primary shadow-inner rounded-sm"
              placeholder="Filter Roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <button
          onClick={() => navigate('/roles/new')}
          className="bg-primary text-white px-8 py-2.5 rounded-sm flex items-center gap-2 hover:bg-primary-dark transition-all mr-4 shadow-lg active:scale-95"
        >
          <Plus size={18} /> CREATE NEW ROLE
        </button>
      </div>

      {/* Grid Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 text-primary animate-pulse">
            <RefreshCw size={40} className="animate-spin mb-4" />
            <span className="tracking-[0.2em]">Loading Security Matrix...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRoles.map((role) => (
              <div
                key={role.role_id}
                onClick={() => navigate(`/roles/${role.role_id}`)}
                className="bg-surface-card border-2 border-line rounded-sm p-5 hover:border-primary hover:shadow-xl cursor-pointer transition-all group relative overflow-hidden"
              >
                {/* Background Decoration */}
                <Shield size={80} className="absolute -right-5 -bottom-5 text-surface-panel group-hover:text-primary-soft transition-colors" />

                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-2 bg-primary-soft rounded-sm text-primary">
                      <Shield size={24} />
                    </div>
                    <span className="text-[9px] bg-surface-panel px-2 py-1 rounded text-ink-secondary italic">
                      ID: #{role.role_id}
                    </span>
                  </div>

                  <h3 className="text-lg font-black text-primary mb-1 group-hover:text-primary-hover transition-colors">
                    {role.name}
                  </h3>

                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-line-subtle">
                    <div className="flex items-center gap-1 text-ink-secondary">
                      <Users size={14} />
                      <span>{role.users?.length || 0} Users</span>
                    </div>
                    <div className="flex items-center gap-1 text-status-warning">
                      <Lock size={14} />
                      <span>Custom Perms</span>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <span className="flex items-center gap-1 text-primary group-hover:translate-x-2 transition-transform">
                      VIEW DETAILS <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {filteredRoles.length === 0 && !loading && (
              <div className="col-span-full p-20 text-center border-4 border-dashed border-line rounded-sm">
                <Lock size={48} className="mx-auto text-line mb-4" />
                <p className="text-ink-secondary font-black italic">No Roles Defined Yet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="bg-primary-dark text-white p-2 px-10 flex justify-between items-center shadow-inner">
        <div className="flex items-center gap-4 text-[9px] italic text-sky tracking-widest uppercase">
          <Shield size={12} />
          Total Defined Roles: {filteredRoles.length}
        </div>
        <div className="text-[9px] text-white/30">
          Maximo Clone Security Module v1.0
        </div>
      </div>
    </div>
  );
};

export default RoleList;